import { useState } from "react";

import { useConfirmationDialogContext } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { assignmentsApi } from "@/api";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { useAssignmentsQuery } from "./useAssignmentsQuery";

export function useAssignments() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const { showYesNoDialog } = useConfirmationDialogContext();
  const { currentUser } = useCurrentUserContext();

  const [showCompleted, setShowCompleted] = useState(false);

  const { data: { open: openAssignments = [], completed: completedAssignments = [] } = {} } = useAssignmentsQuery(
    currentUser.counselorId,
    showCompleted
  );

  const { mutate: deleteAllCompleted, isPending: isDeletingAllCompleted } = useMutation({
    mutationFn: () => assignmentsApi.deleteAllCompletedAssignments(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] });
      showSuccess(t("assignments.editDialog.successfullyDeleted"));
    },
  });

  const handleDeleteAllCompleted = () => {
    showYesNoDialog(
      t("assignments.editDialog.deleteAllCompletedTitle"),
      t("assignments.editDialog.deleteAllCompletedConfirmText"),
      (confirmed) => {
        if (confirmed) {
          deleteAllCompleted();
        }
      }
    );
  };

  return {
    openAssignments,
    completedAssignments,
    handleDeleteAllCompleted,
    isDeletingAllCompleted,
    showCompleted,
    setShowCompleted,
  };
}
